/**
 * Whether the Meta Pixel may load before the visitor has said so.
 *
 * In the EU/EEA, the UK and Switzerland a measurement cookie needs consent
 * before it is set (ePrivacy Directive art. 5(3), UK PECR reg. 6, Swiss FADP).
 * Elsewhere — the United States and the UAE included, which is where Adkiro
 * sells — it does not, and a consent wall there is friction that buys
 * nothing. So the decision is made per request from the country Cloudflare
 * stamps on it, and a visitor's own answer, once given, outranks geography.
 *
 * Pure and isomorphic: the server uses it to decide what goes in the HTML,
 * the server functions use it to decide whether a Conversions API event may
 * be sent, and the tests exercise it directly.
 */

export const CONSENT_COOKIE = "adk_consent";

/** A year for "yes"; the visitor asked for it to be remembered. */
export const CONSENT_GRANTED_MAX_AGE = 60 * 60 * 24 * 365;
/** Six months for "no": long enough not to nag, short enough to ask again. */
export const CONSENT_DENIED_MAX_AGE = 60 * 60 * 24 * 182;

export type ConsentMode = "allowed" | "ask" | "denied";

const CONSENT_REGIONS = new Set([
  // EU 27
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA
  "IS",
  "LI",
  "NO",
  // Same rule under a different statute
  "GB",
  "CH",
]);

/**
 * Cloudflare sends "XX" when it cannot place the address and "T1" for Tor.
 * Neither is "outside Europe", so both are treated as unknown.
 */
const UNKNOWN_COUNTRIES = new Set(["XX", "T1"]);

export function requiresConsent(country: string | undefined | null): boolean {
  // No country means the request did not pass through Cloudflare — local
  // development, or an edge misconfiguration. Asking is the only answer that
  // is never wrong.
  if (!country) return true;
  const code = country.trim().toUpperCase();
  if (UNKNOWN_COUNTRIES.has(code)) return true;
  return CONSENT_REGIONS.has(code);
}

export function resolveConsent(
  country: string | undefined | null,
  cookie: string | undefined | null,
): ConsentMode {
  if (cookie === "granted") return "allowed";
  if (cookie === "denied") return "denied";
  return requiresConsent(country) ? "ask" : "allowed";
}
